'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';

/**
 * Компонент ImageGallery для відображення галереї зображень
 * @param {Object[]} images - Масив об'єктів зображень
 * @param {string} images[].src - URL зображення
 * @param {string} images[].alt - Альтернативний текст зображення
 * @param {string} images[].caption - Підпис до зображення (опціонально)
 * @param {boolean} autoplay - Чи автоматично змінювати зображення
 * @param {number} autoplaySpeed - Швидкість автоматичної зміни в мс (за замовчуванням 5000мс)
 * @param {boolean} thumbnails - Чи показувати мініатюри під основним зображенням
 */
export default function ImageGallery ({ 
  images = [], 
  autoplay = false, 
  autoplaySpeed = 5000, 
  thumbnails = true 
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Функції для навігації - обгорнуті в useCallback
  const goToPrevious = useCallback(() => {
    const isFirstImage = currentIndex === 0;
    const newIndex = isFirstImage ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
  }, [currentIndex, images.length]);

  const goToNext = useCallback(() => {
    const isLastImage = currentIndex === images.length - 1;
    const newIndex = isLastImage ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
  }, [currentIndex, images.length]);

  // Обробка автоматичної прокрутки, якщо увімкнено
  useEffect(() => {
    if (autoplay) {
      const interval = setInterval(() => {
        goToNext();
      }, autoplaySpeed);
      
      return () => clearInterval(interval);
    }
  }, [autoplay, autoplaySpeed, goToNext]);

  // Імітуємо завантаження зображень
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [currentIndex]);

  // Обробка перетягування (свайпів) для мобільних пристроїв
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };
  
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  
  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      // Свайп вліво - наступне зображення
      goToNext();
    }
    
    if (touchStart - touchEnd < -50) {
      // Свайп вправо - попереднє зображення
      goToPrevious();
    }
  };

  // Обробка клавіш стрілок для навігації
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isModalOpen) return;
      
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        setIsModalOpen(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isModalOpen, currentIndex, goToNext, goToPrevious]);

  // Якщо немає зображень, не рендеримо галерею
  if (images.length === 0) {
    return null;
  }

  return (
    <div className="my-8">
      <div 
        className="relative overflow-hidden rounded-lg aspect-w-16 aspect-h-9 bg-gray-100 cursor-pointer shadow-md"
        onClick={() => setIsModalOpen(true)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Індикатор завантаження */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white bg-opacity-75">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        )}
        
        {/* Основне зображення */}
        <Image
          src={images[currentIndex]?.src || '/images/placeholder.jpg'} 
          alt={images[currentIndex]?.alt || 'Галерея зображень'} 
          width={800}
          height={600}
          className={`w-full h-full object-contain transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          onLoad={() => setIsLoading(false)}
        />
        
        {/* Навігаційні кнопки */}
        {images.length > 1 && (
          <>
            <button
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full focus:outline-none transition-all z-20"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full focus:outline-none transition-all z-20"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            {/* Індикатори (точки) поточного зображення */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
              {images.map((_, index) => (
                <button
                  key={index}
                  className={`w-2.5 h-2.5 rounded-full focus:outline-none transition-all ${currentIndex === index ? 'bg-white' : 'bg-white bg-opacity-50'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(index);
                  }}
                />
              ))}
            </div>
          </>
        )}
        
        {/* Підпис до зображення */}
        {images[currentIndex]?.caption && (
          <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-3 text-sm z-10">
            {images[currentIndex].caption}
          </div>
        )}
      </div>
      
      {/* Мініатюри зображень */}
      {thumbnails && images.length > 1 && (
        <div className="flex overflow-x-auto mt-3 space-x-2 pb-2">
          {images.map((image, index) => (
            <div
              key={index}
              className={`flex-shrink-0 cursor-pointer transition-all ${
                currentIndex === index 
                  ? 'ring-2 ring-blue-500 opacity-100' 
                  : 'opacity-70 hover:opacity-100'
              }`}
              onClick={() => setCurrentIndex(index)}
            >
              <Image
                src={image.src || '/images/placeholder.jpg'}
                alt={`Мініатюра ${index + 1}`}
                width={100}
                height={100}
                className="w-full h-full object-cover rounded"
              />
            </div>
          ))}
        </div>
      )}
      
      {/* Модальне вікно для повноекранного перегляду */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
          onClick={() => setIsModalOpen(false)}
        >
          <div 
            className="relative max-w-6xl max-h-screen p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all z-50"
              onClick={() => setIsModalOpen(false)}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="relative h-screen flex items-center justify-center">
              <Image
                src={images[currentIndex]?.src || '/images/placeholder.jpg'} 
                alt={images[currentIndex]?.alt || 'Галерея зображень'} 
                width={800}
                height={600}
                className="max-h-screen max-w-full object-contain"
              />
              
              {/* Навігаційні кнопки в модальному вікні */}
              {images.length > 1 && (
                <>
                  <button
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-4 rounded-full focus:outline-none transition-all"
                    onClick={goToPrevious}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-4 rounded-full focus:outline-none transition-all"
                    onClick={goToNext}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}
              
              {/* Підпис до зображення в модальному вікні */}
              {images[currentIndex]?.caption && (
                <div className="absolute bottom-8 left-0 right-0 text-center text-white bg-black bg-opacity-50 p-3">
                  {images[currentIndex].caption}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};